import * as amplitude from '@amplitude/analytics-browser';

import type { CustomDestination } from '../Avo';

const amplitudeDestinationInterface: CustomDestination = {
  make: (_env: unknown, apiKey: string) =>
    amplitude.init(apiKey, {
      serverZone: 'EU',
      autocapture: {
        attribution: false,
        pageViews: false,
        sessions: false,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
      trackingOptions: {
        ipAddress: false,
      },
    }),

  identify: (userId: string) => {
    amplitude.setUserId(userId);
    amplitude.setOptOut(false);
  },

  logEvent: (eventName: string, eventProperties: Record<string, unknown>) =>
    amplitude.track(eventName, eventProperties),

  logPage: (pageUrl: string) => {
    amplitude.track('PageView', { 'Page URL': pageUrl });
  },

  setUserProperties: (_userId: string, userProperties: Record<string, unknown>) => {
    const identify = new amplitude.Identify();

    Object.entries(userProperties).forEach(([key, value]) => identify.set(key, value as string | number | boolean));
    amplitude.identify(identify);
  },

  unidentify: () => {
    amplitude.setOptOut(true);
  },

  revenue: (amount: number, eventProperties: Record<string, unknown>) => {
    const event = new amplitude.Revenue().setPrice(amount);

    if (eventProperties['quantity']) {
      event.setQuantity(eventProperties['quantity'] as number);
    }

    if (eventProperties['productId']) {
      event.setProductId(eventProperties['productId'] as string);
    }

    if (eventProperties['revenueType']) {
      event.setRevenueType(eventProperties['revenueType'] as string);
    }

    if (Object.keys(eventProperties).length > 0) {
      event.setEventProperties(eventProperties as Record<string, string | number | boolean>);
    }

    amplitude.revenue(event);
  },

  setGroupProperties: (groupTypeName: string, groupId: string, groupProperties: Record<string, unknown>) => {
    const group = new amplitude.Identify();

    Object.entries(groupProperties).forEach(([key, value]) => group.set(key, value as string | number | boolean));
    amplitude.groupIdentify(groupTypeName, groupId, group);
  },

  addCurrentUserToGroup: (groupTypeName: string, groupId: string) => amplitude.setGroup(groupTypeName, groupId),

  logEventWithGroups: (
    eventName: string,
    eventProperties: Record<string, unknown>,
    groupTypeNamesToGroupIds: Record<string, string>,
  ) =>
    amplitude.track({
      event_type: eventName,
      event_properties: eventProperties,
      groups: groupTypeNamesToGroupIds,
    }),
};

export default amplitudeDestinationInterface;
